/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import HistoryIcon from '@mui/icons-material/History'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { automationsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import PageHeader from '../../components/common/PageHeader'
import EmptyState from '../../components/common/EmptyState'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import Loading from '../../components/common/Loading'
import { useNotify } from '../../components/common/NotificationProvider'
import RuleSummary from '../../components/automation/RuleSummary'
import RunHistoryDrawer from '../../components/automation/RunHistoryDrawer'
import { entityLabelPlural, formatRelativeTime } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'

const Stats = ({ stats }) => {
  if (!stats.runs) return <Typography variant="caption" color="text.secondary">No runs in the last 30 days</Typography>
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {stats.runs} {stats.runs === 1 ? 'run' : 'runs'} in 30 days · last {formatRelativeTime(stats.lastRunOn)}
      </Typography>
      {stats.failures > 0 && <Chip label={`${stats.failures} failed`} color="error" variant="outlined" />}
    </Stack>
  )
}

const AutomationsPage = () => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const notify = useNotify()
  const queryClient = useQueryClient()
  const [history, setHistory] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const { data: rules = [], isLoading, error } = useQuery({ queryKey: [tenantId, 'automations'], queryFn: () => automationsAPI.list() })
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'automations'] })

  const toggle = useMutation({
    mutationFn: ({ id, enabled }) => automationsAPI.setEnabled(id, enabled),
    onSuccess: (rule) => { refresh(); notify(`${rule.name} turned ${rule.enabled ? 'on' : 'off'}`) },
    onError: (e) => notify([getErrorMessage(e), ...getErrorList(e)].join(' · '), 'error'),
  })
  const remove = useMutation({
    mutationFn: (id) => automationsAPI.remove(id),
    onSuccess: () => { setDeleting(null); refresh(); notify('Automation deleted') },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  const groups = rules.reduce((acc, r) => ({ ...acc, [r.entityType]: [...(acc[r.entityType] || []), r] }), {})

  return (
    <>
      <PageHeader title="Automations" subtitle="Steps that run by themselves when records change"
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/settings/automations/new')}>New automation</Button>} />
      {isLoading && <Loading />}
      {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
      {!isLoading && !error && rules.length === 0 && (
        <Paper variant="outlined">
          <EmptyState icon={<BoltOutlinedIcon />} title="No automations yet"
            description="For example: when a deal’s stage changes to Won, set the close date, create an onboarding task and notify the owner."
            action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/settings/automations/new')}>New automation</Button>} />
        </Paper>
      )}
      <Stack spacing={3}>
        {Object.entries(groups).map(([entityType, list]) => (
          <Box key={entityType}>
            <Typography variant="overline" color="text.secondary">{entityLabelPlural(entityType)}</Typography>
            <Paper variant="outlined">
              {list.map((rule, i) => (
                <Stack key={rule.id} direction="row" spacing={1.5} alignItems="center"
                  sx={{ px: 2, py: 1.5, borderTop: i ? 1 : 0, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => navigate(`/settings/automations/${rule.id}`)}>
                  <Tooltip title={rule.enabled ? 'On: turn off' : 'Off: turn on'}>
                    <Switch checked={rule.enabled} onClick={(e) => e.stopPropagation()} disabled={toggle.isPending}
                      onChange={(e) => toggle.mutate({ id: rule.id, enabled: e.target.checked })} inputProps={{ 'aria-label': `${rule.name} on` }} />
                  </Tooltip>
                  <Box sx={{ flexGrow: 1, minWidth: 0, opacity: rule.enabled ? 1 : 0.6 }}>
                    <Typography variant="subtitle2" noWrap>{rule.name}</Typography>
                    <RuleSummary rule={rule} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} />
                    <Box sx={{ mt: 0.5 }}><Stats stats={rule.stats} /></Box>
                  </Box>
                  <Tooltip title="Run history">
                    <IconButton onClick={(e) => { e.stopPropagation(); setHistory(rule) }} aria-label={`Run history of ${rule.name}`}><HistoryIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={(e) => { e.stopPropagation(); setDeleting(rule) }} aria-label={`Delete ${rule.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Paper>
          </Box>
        ))}
      </Stack>
      <RunHistoryDrawer rule={history} onClose={() => setHistory(null)} />
      <ConfirmDialog open={!!deleting} title={`Delete “${deleting?.name}”?`} message="It stops running immediately. Its run history is kept."
        confirmLabel="Delete" destructive busy={remove.isPending} onConfirm={() => remove.mutate(deleting.id)} onClose={() => setDeleting(null)} />
    </>
  )
}

export default AutomationsPage
